"use client";

import { Button } from "@pex/ui-web/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@pex/ui-web/components/ui/command";
import { Input } from "@pex/ui-web/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@pex/ui-web/components/ui/popover";
import {
  countryFromCallingCode,
  countryFromValue,
  parsePhone,
  toE164,
} from "@pex/ui-web/lib/phone-utils";
import { cn } from "@pex/ui-web/lib/utils";
import { AsYouType } from "libphonenumber-js";
import { Check, ChevronDown, Globe } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  type Country,
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import labels from "react-phone-number-input/locale/en.json";
import "./phone-field.css";

const COUNTRY_LABELS = labels as Record<string, string>;
const COUNTRY_OPTIONS = getCountries()
  .map((value) => ({ label: COUNTRY_LABELS[value] ?? value, value }))
  .sort((a, b) => a.label.localeCompare(b.label));

interface PhoneFieldProps {
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  autoFocus?: boolean;
  /**
   * Extra classes applied to the country-selector button. Defaults to the
   * shared pill `Button` look. Merged last so callers can override (e.g. to
   * match an underline or borderless host form).
   */
  buttonClassName?: string;
  className?: string;
  defaultCountry?: Country;
  disabled?: boolean;
  id?: string;
  /**
   * Extra classes applied to the number input. Defaults to the shared pill
   * `Input` look. Merged last so callers can override.
   */
  inputClassName?: string;
  onBlur?: () => void;
  onChange: (value: string) => void;
  onEnter?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  value: string;
}

function CountryFlag({ country }: { country: Country }) {
  const Flag = flags[country];
  if (!Flag) {
    return <Globe className="size-4 text-muted-foreground" />;
  }
  return (
    <span className="flex h-4 w-[1.4rem] shrink-0 items-center justify-center overflow-hidden rounded-[3px]">
      <Flag title={country} />
    </span>
  );
}

interface CountrySelectProps {
  buttonClassName?: string;
  disabled?: boolean;
  onChange: (value: Country) => void;
  value?: Country;
}

function CountrySelect({
  buttonClassName,
  disabled,
  onChange,
  value,
}: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const callingCode = value ? getCountryCallingCode(value) : null;

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-label="Phone number country"
          className={cn(
            "h-9 shrink-0 gap-1.5 rounded-4xl border border-input bg-input/30 px-3 font-normal text-base hover:bg-input/50 md:text-sm",
            buttonClassName
          )}
          disabled={disabled}
          role="combobox"
          type="button"
          variant="outline"
        >
          {value ? (
            <CountryFlag country={value} />
          ) : (
            <Globe className="size-4 text-muted-foreground" />
          )}
          {callingCode ? (
            <span className="text-muted-foreground tabular-nums">
              +{callingCode}
            </span>
          ) : null}
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[18rem] p-0">
        <Command>
          <CommandInput placeholder="Search country…" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRY_OPTIONS.map((opt) => {
                const code = getCountryCallingCode(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    onSelect={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    value={`${opt.label} ${opt.value} +${code}`}
                  >
                    <CountryFlag country={opt.value} />
                    <span className="flex-1 truncate">{opt.label}</span>
                    <span className="text-muted-foreground text-xs tabular-nums">
                      +{code}
                    </span>
                    {opt.value === value ? (
                      <Check className="size-4 shrink-0" />
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function PhoneField({
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  autoFocus,
  buttonClassName,
  className,
  defaultCountry,
  disabled,
  id,
  inputClassName,
  onBlur,
  onChange,
  onEnter,
  onKeyDown,
  placeholder,
  value,
}: PhoneFieldProps) {
  const [country, setCountry] = useState<Country | undefined>(
    () => countryFromValue(value) ?? defaultCountry
  );

  // The input always renders `displayValue`, never the prop `value`. This
  // prevents the displayed string from being rewritten mid-type (which would
  // reset cursor position). E.164 is still emitted to the parent via onChange
  // on every keystroke so the parent's state is always clean before any
  // button handler fires.
  const [displayValue, setDisplayValue] = useState(value);
  const isEditing = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // True once the user has typed or picked a country in this session, so
  // defaultCountry resolution stops trying to "adopt" itself over a real choice.
  const userHasInteracted = useRef(false);

  // Adopt an async-resolved defaultCountry while the field is still empty AND
  // the user hasn't already made a deliberate choice. Without the interaction
  // guard, this effect would re-fire after the user picks a country (because
  // clearing the field on switch flips `value` back to ""), instantly resetting
  // the flag to the default and looking like the first click did nothing.
  useEffect(() => {
    if (defaultCountry && !value && !userHasInteracted.current) {
      setCountry(defaultCountry);
    }
  }, [defaultCountry, value]);

  // Sync the display (and flag) from the prop when an external change arrives
  // — e.g. form reset or pre-filled data — but only while the user isn't
  // actively editing, so user input is never clobbered mid-type.
  useEffect(() => {
    if (!isEditing.current) {
      setDisplayValue(value);
      const detected = countryFromValue(value);
      if (detected) {
        setCountry(detected);
      }
    }
  }, [value]);

  const handleCountryChange = (next: Country) => {
    userHasInteracted.current = true;
    setCountry(next);
    // Reset the field on country switch — a number typed for one country
    // generally isn't valid for another. The dial code stays visible in the
    // selector button so the user knows what to type next.
    setDisplayValue("");
    onChange("");
    // Return focus to the input so the user can immediately type the number
    // for the new country (Popover's default is to focus back on its trigger).
    inputRef.current?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    userHasInteracted.current = true;
    const raw = e.target.value;
    setDisplayValue(raw); // mirror user input verbatim — no cursor jump

    // Parse once for both E.164 emission and country detection. The current
    // country is the hint for national-format input ("0821234567" → ZA); a
    // "+XX" prefix overrides the hint.
    const { number: emitValue, country: parsedCountry } = parsePhone(
      raw,
      country
    );
    let detectedCountry = parsedCountry;

    // For partial international input ("+44") that's still too short for
    // parsePhone to commit to a country, AsYouType exposes the recognised
    // dial code right away via getCallingCode(). Map that to a country
    // ourselves — getCountry() itself only resolves once the full national
    // number has been entered.
    if (!detectedCountry && raw.startsWith("+")) {
      const formatter = new AsYouType();
      formatter.input(raw);
      const code = formatter.getCallingCode();
      if (code) {
        detectedCountry = countryFromCallingCode(code, country);
      }
    }

    onChange(emitValue);
    if (detectedCountry && detectedCountry !== country) {
      setCountry(detectedCountry);
    }
  };

  const handleFocus = () => {
    isEditing.current = true;
  };

  const handleBlur = () => {
    isEditing.current = false;
    // On leaving the field, rewrite the display to E.164 for visual clarity
    // (e.g. "0821234567" → "+27821234567"). The parent already has the
    // canonical value from handleChange; this only catches the display.
    const normalised = toE164(displayValue, country);
    if (normalised !== displayValue) {
      setDisplayValue(normalised);
      onChange(normalised);
    }
    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (!e.defaultPrevented && e.key === "Enter" && onEnter) {
      e.preventDefault();
      onEnter();
    }
  };

  return (
    <div className={cn("pex-phone-field", className)}>
      <CountrySelect
        buttonClassName={buttonClassName}
        disabled={disabled}
        onChange={handleCountryChange}
        value={country}
      />
      <Input
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid}
        autoFocus={autoFocus}
        className={cn("min-w-0 flex-1", inputClassName)}
        disabled={disabled}
        id={id}
        inputMode="tel"
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={inputRef}
        type="tel"
        value={displayValue}
      />
    </div>
  );
}
